// One reminder, drawn the same way wherever it appears.
//
// ONE COMPONENT for the block on the day and for the full list, because they
// are the same rows: two components would be two answers to what an overdue
// reminder looks like, and the whole feature rests on the two surfaces agreeing.
//
// It is an `.item` like a schedule row, on purpose - the same tick, the same
// meta line, the same strike-through - because the difference between the two
// is carried by WHERE it sits and not by how it is drawn: a schedule item is in
// a coloured card, and a reminder is in the one card with no hue. Colour
// identifies a schedule; the absence of colour identifies the thing that has
// none (prototype-v2.md section 1.7).
//
// The row's TEXT opens the reminder's one routine row. It is kept separate from
// both the tick and the full-list Remove action, so each accessible control has
// one job and a name that says which reminder it acts on.

import type { IsoDay } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { reminderMeta } from './reminders-model.ts';
import type { ReminderMeta, ReminderView } from './reminders-model.ts';

interface Props {
  reminder: ReminderView;
  /**
   * The account's today: every date on this row is relative to it. Null while
   * it is unknown, and then the row simply carries no dates - this client never
   * works out what day it is (AGENTS.md).
   */
  today: IsoDay | null;
  /**
   * Ticks it off, or takes the tick back. A row that is HISTORY never gets
   * one - see below - and a row whose day is not known yet gets it disabled.
   */
  onToggle: ((id: string) => void) | null;
  /** Opens the one routine row for editing. */
  onEdit?: ((id: string) => void) | undefined;
  /** Takes it off the list, with an Undo. The full list only. */
  onRemove?: ((id: string, title: string) => void) | undefined;
}

function ReminderText({
  reminder,
  meta,
  history,
}: {
  reminder: ReminderView;
  meta: ReminderMeta;
  history: boolean;
}) {
  return (
    <>
      <span className="item-title">{reminder.title}</span>
      {(meta.words !== '' || reminder.note !== '') && (
        <span className="item-meta">
          {/* History replaces the due date rather than stacking with it: this
              line carries at most two signals. */}
          {history && <Icon name="history" />}
          {meta.words !== '' && (
            <span className={meta.kind === 'due' && meta.overdue ? 'was' : undefined}>
              {meta.words}
            </span>
          )}
          {reminder.note !== '' && (
            <span className="notedot">
              <Icon name="note" />
              <span className="sr">has a note</span>
            </span>
          )}
        </span>
      )}
    </>
  );
}

export function ReminderRow({ reminder, today, onToggle, onEdit, onRemove }: Props) {
  const meta = reminderMeta(reminder, today);
  // HISTORY IS NOT A CONTROL. A reminder is finished once, so a tick on a day
  // it was merely open on is refused by the server (`already-finished`) - and a
  // checkbox that always fails is the trap this client has been caught by five
  // times, wearing a checkbox. There is nothing to do on this row, so there is
  // nothing to press on it: a static marker where the tick would be, and the
  // sentence beside it saying when it was in fact finished.
  const history = meta.kind === 'history';

  return (
    <li
      className={[
        'item',
        reminder.done ? 'done' : '',
        history ? 'history' : '',
        onRemove !== undefined ? 'removable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {history ? (
        // Drawn UNTICKED and not struck through, because that is how the day
        // held it: a reminder is on every day it was open on, presented as it
        // was open then, and finishing it later marks it rather than erasing it
        // from those days (api/src/domain/day.ts).
        <span className="tick static" aria-hidden="true">
          <i />
        </span>
      ) : (
        // `role="checkbox"` with `aria-checked` rather than a real input, as
        // every other tick in this client: the circle and its fill are drawn,
        // and a native checkbox brings a control that cannot be. Its name is
        // the title alone - the rest of the row is ordinary text right beside
        // it, and a label repeating it would be said twice.
        <button
          className="tick"
          role="checkbox"
          aria-checked={reminder.done}
          aria-label={reminder.title}
          disabled={onToggle === null}
          onClick={() => onToggle?.(reminder.id)}
        >
          <i />
        </button>
      )}

      <div className="item-main">
        {onEdit === undefined ? (
          <div className="item-txt">
            <ReminderText reminder={reminder} meta={meta} history={history} />
          </div>
        ) : (
          // The same text target the scheduled-item row uses. The explicit
          // verb distinguishes it from the checkbox with the same title for a
          // screen reader, and only the text is a button - never a button
          // wrapped around the tick or Remove action beside it.
          <button
            className="item-txt"
            aria-label={[
              `Edit ${reminder.title}`,
              meta.words,
              reminder.note !== '' ? 'has a note' : '',
            ]
              .filter(Boolean)
              .join(', ')}
            onClick={() => onEdit(reminder.id)}
          >
            <ReminderText reminder={reminder} meta={meta} history={history} />
          </button>
        )}
      </div>

      {onRemove !== undefined && (
        // Named for the reminder it acts on: a column of buttons whose visible
        // glyph is the same one says which control exists and not which row it
        // takes away, exactly as `Restore <name>` does in Settings.
        <button
          className="item-remove"
          aria-label={`Remove ${reminder.title}`}
          onClick={() => onRemove(reminder.id, reminder.title)}
        >
          <Icon name="trash" />
        </button>
      )}
    </li>
  );
}
